/**
 * חשבוניות וגבייה — "מי חייב לי, כמה זמן, ומה אני עושה עם זה עכשיו" (spec-business §4.3).
 *
 * A: 4 אריחים · B: דליי גיל (C6, 5) + לפי לקוח (7) · C: רשימת חשבוניות (12) + drawer.
 * Actions: "שלח תזכורת" produces a DRAFT only (copy / mailto — the system never sends),
 * "סמן כשולם" opens the paid dialog with the unmatched-receipt picker, and "הוצא זיכוי" is
 * documented as 501 (spec §9 — credits are issued in HYP).
 */

import { useState } from 'react';
import type { Invoice, InvoiceDetail, InvoicesQuery, InvoicesResponse, ReceiptCandidate, SummaryResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtShortDate } from '../format.ts';
import { DOC_TYPE, INVOICE_STATUS, PAYMENT_METHOD, he } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import {
  Card,
  CardHead,
  Chip,
  EmptyState,
  ErrorBox,
  FilterChip,
  Ltr,
  Money,
  Num,
  SearchField,
  SelectField,
  Skeleton,
  SortableTh,
  Tile,
  toggleSort,
} from '../components/ui.tsx';
import { BarList } from '../charts/BarList.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';
import { errorMessage } from '../api-select.ts';

type SortCol = NonNullable<InvoicesQuery['sort']>;

function StatusChip({ invoice }: { invoice: Invoice }) {
  const meta = INVOICE_STATUS[invoice.status];
  const label =
    invoice.status === 'paid'
      ? he.invoices.paidOn(fmtDate(invoice.paidAt))
      : invoice.status === 'overdue'
        ? he.invoices.overdueDays(invoice.overdueDays ?? 0)
        : invoice.status === 'credited'
          ? DOC_TYPE.credit.short
          : he.invoices.filterOpen;
  return (
    <Chip tone={meta.chip as 'good' | 'critical' | 'info' | 'outline'} icon={meta.icon}>
      {label}
    </Chip>
  );
}

export function InvoicesScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, toast, openDrawer, openDialog, closeLayer } = useApp();
  const { route, navigate } = useRouter();
  const [status, setStatus] = useState<'all' | Invoice['status']>((route.query.status as Invoice['status']) ?? 'all');
  const [customer, setCustomer] = useState(route.query.customer ?? 'all');
  const [bucket, setBucket] = useState(route.query.bucket ?? '');
  const [range, setRange] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'issue', dir: 'desc' });

  const query: InvoicesQuery = {
    status,
    customer: customer === 'all' ? undefined : customer,
    bucket: (bucket || undefined) as InvoicesQuery['bucket'],
    from: range === 'all' ? undefined : range,
    q: q || undefined,
    sort: sort.col,
  };
  const invoices = useQuery<InvoicesResponse>(cacheKey('invoices', { ...query }), () => api.invoices(query), { scopes: ['invoices'] });
  const asOf = summary?.asOf ?? '';
  const data = invoices.data;

  const openInvoice = (number: string) => {
    api.invoice(number).then(
      (detail) => openDrawer({ label: `${DOC_TYPE[detail.docType].label} ${detail.number}`, title: <InvoiceDrawerTitle detail={detail} />, content: <InvoiceDrawerBody detail={detail} />, footer: <InvoiceDrawerFooter detail={detail} /> }),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  function InvoiceDrawerTitle({ detail }: { detail: InvoiceDetail }) {
    return (
      <>
        {DOC_TYPE[detail.docType].label} <Ltr>{detail.number}</Ltr>
        <small>
          {detail.customerName}
          {detail.label ? ` · ${detail.label}` : ''}
        </small>
      </>
    );
  }

  function InvoiceDrawerBody({ detail }: { detail: InvoiceDetail }) {
    return (
      <>
        <div className="inline">
          <StatusChip invoice={detail} />
          <Chip tone="outline">{PAYMENT_METHOD[detail.method]}</Chip>
          {detail.docType === 'proforma' ? (
            <Chip tone="outline" icon="info">
              {he.invoices.proformaNote}
            </Chip>
          ) : null}
        </div>
        <dl className="kv">
          <dt>{he.invoices.issueDate}</dt>
          <dd>
            <Num>{fmtDate(detail.issueDate)}</Num>
          </dd>
          <dt>{he.invoices.dueDate}</dt>
          <dd>
            {detail.method === 'transfer' ? (
              <>
                <Num>{fmtDate(detail.dueDate)}</Num>
                {detail.status === 'overdue' ? (
                  <>
                    {' '}
                    <Chip tone="critical" small>
                      {he.invoices.lateDays(detail.overdueDays ?? 0)}
                    </Chip>
                  </>
                ) : null}
              </>
            ) : (
              he.invoices.immediate
            )}
          </dd>
          {detail.paidAt ? (
            <>
              <dt>{he.invoices.paidAt}</dt>
              <dd>
                <Num>{fmtDate(detail.paidAt)}</Num>
              </dd>
            </>
          ) : null}
          <dt>{he.invoices.customer}</dt>
          <dd>
            {detail.customer.name}
            {detail.customer.email ? (
              <>
                {' · '}
                <Ltr>{detail.customer.email}</Ltr>
              </>
            ) : null}
          </dd>
          <dt>{he.invoices.terms}</dt>
          <dd>{detail.customer.paymentTerms === 'net30' ? he.invoices.termsNet30 : he.invoices.termsImmediate}</dd>
        </dl>
        <div className="rows">
          <div className="row">
            <span className="lbl">{he.invoices.net}</span>
            <span className="val">
              <Money agorot={detail.netAgorot} decimals={2} />
            </span>
          </div>
          <div className="row">
            <span className="lbl">{he.invoices.vatRate('18%')}</span>
            <span className="val">
              <Money agorot={detail.vatAgorot} decimals={2} />
            </span>
          </div>
          <div className="row total">
            <span className="lbl">{he.invoices.grossTotal}</span>
            <span className="val">
              <Money agorot={detail.grossAgorot} decimals={2} />
            </span>
          </div>
        </div>
        <div>
          <div className="kicker">{he.invoices.reminderHistory}</div>
          <div className="reminder-hist">
            {detail.reminderHistory.length ? (
              detail.reminderHistory.map((reminder) => (
                <span key={reminder.id}>
                  <Icon name="mail" className="xs" /> {he.invoices.reminderSent(fmtDate(reminder.day))}
                </span>
              ))
            ) : (
              <span className="note">{he.invoices.noReminders}</span>
            )}
          </div>
        </div>
      </>
    );
  }

  function InvoiceDrawerFooter({ detail }: { detail: InvoiceDetail }) {
    return (
      <>
        {detail.status !== 'paid' ? (
          <>
            <button type="button" className="btn primary" onClick={() => openReminder(detail)}>
              <Icon name="send" />
              {he.invoices.sendReminder}
            </button>
            <button type="button" className="btn" onClick={() => openMarkPaid(detail)}>
              <Icon name="check" />
              {he.invoices.markPaid}
            </button>
          </>
        ) : null}
        <button type="button" className="btn ghost" onClick={() => toast(he.invoices.creditSoon)}>
          <Icon name="rotate" />
          {he.invoices.issueCredit}
        </button>
      </>
    );
  }

  const openReminder = (invoice: InvoiceDetail) => {
    api.reminderDraft(invoice.number).then(
      (draft) => {
        closeLayer();
        openDialog({
          title: he.invoices.reminderTitle(invoice.number),
          wide: true,
          content: (
            <>
              {draft.warning ? <p className="note">{draft.warning}</p> : null}
              <div className="field">
                <label htmlFor="rem-to">{he.invoices.reminderTo}</label>
                <input id="rem-to" className="input ltr" dir="ltr" defaultValue={draft.to.join(', ')} readOnly />
              </div>
              <div className="field">
                <label htmlFor="rem-subject">{he.invoices.reminderSubject}</label>
                <input id="rem-subject" className="input" defaultValue={draft.subject} readOnly />
              </div>
              <div className="field">
                <label htmlFor="rem-body">{he.invoices.reminderBody}</label>
                <textarea id="rem-body" className="input" defaultValue={draft.body} />
              </div>
            </>
          ),
          actions: [
            {
              label: he.invoices.copyDraft,
              tone: 'primary',
              onClick: () => {
                const text = (document.getElementById('rem-body') as HTMLTextAreaElement | null)?.value ?? draft.body;
                navigator.clipboard?.writeText(text).then(
                  () => {
                    toast(he.invoices.copied);
                    void api.recordReminder(invoice.number, 'copy').catch(() => undefined);
                  },
                  () => toast(he.invoices.copyFailed, { error: true }),
                );
              },
            },
            {
              label: he.invoices.openMail,
              onClick: () => {
                window.location.href = draft.mailto;
                void api.recordReminder(invoice.number, 'mailto').catch(() => undefined);
              },
            },
            { label: he.app.cancel },
          ],
        });
      },
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const openMarkPaid = (invoice: InvoiceDetail) => {
    let chosenTx: string | null = invoice.receiptCandidates[0]?.txId ?? null;
    let paidAt = asOf;
    closeLayer();
    openDialog({
      title: he.invoices.markPaidTitle(invoice.number),
      content: (
        <>
          <div className="field">
            <label htmlFor="paid-date">{he.invoices.paidDate}</label>
            <input id="paid-date" className="input" type="date" defaultValue={asOf} max={asOf} onChange={(event) => (paidAt = event.currentTarget.value)} />
          </div>
          <div className="field">
            <label>{he.invoices.matchReceipt}</label>
            {invoice.receiptCandidates.length ? (
              <ReceiptPicker candidates={invoice.receiptCandidates} onChange={(value) => (chosenTx = value)} />
            ) : (
              <p className="note">{he.invoices.noReceipt}</p>
            )}
          </div>
        </>
      ),
      actions: [
        {
          label: he.invoices.markPaid,
          tone: 'primary',
          onClick: () => {
            api.payInvoice(invoice.number, { paidAt, txId: chosenTx }).then(
              () => toast(he.invoices.markedPaid(invoice.number)),
              (error: unknown) => toast(errorMessage(error), { error: true }),
            );
          },
        },
        { label: he.app.cancel },
      ],
    });
  };

  return (
    <div className="grid invoices">
      <div className="col-12 tiles t4 tiles-a">
        {/* the screen's ONE hero figure (REDESIGN §8) */}
        <Tile hero label={he.invoices.open} value={<Money agorot={data?.summary.openAgorot ?? 0} />} note={he.invoices.openNote(data?.summary.openCount ?? 0)} />
        <Tile
          tone="critical"
          onClick={() => setStatus('overdue')}
          label={
            <>
              {he.invoices.overdue} <Icon name="alertCircle" className="sm" />
            </>
          }
          value={<Money agorot={data?.summary.overdueAgorot ?? 0} />}
          note={
            <>
              {he.invoices.overdueNote(data?.summary.overdueCount ?? 0)}
              {data?.summary.oldestOverdue ? ` · ${he.invoices.oldest(data.summary.oldestOverdue.days)}` : ''}
            </>
          }
        />
        <Tile
          label={he.invoices.collected}
          value={<Money agorot={data?.summary.collectedThisMonthAgorot ?? 0} />}
          note={he.invoices.collectedNote(summary ? `1–${summary.period.dayOfMonth}` : '')}
        />
        <Tile
          label={he.invoices.avgDays}
          value={
            <>
              <Num>{data?.summary.avgDaysToPay ?? '—'}</Num>
              <span className="unit">{he.app.days}</span>
            </>
          }
          note={he.invoices.avgDaysNote}
        />
      </div>

      {/* ── דליי גיל (C6) ──────────────────────────────────────────── */}
      <Card span={5} className="buckets" labelledBy="h-bk">
        <CardHead titleId="h-bk" title={he.invoices.agingQuestion} sub={he.invoices.agingSub} />
        <BarList
          ariaLabel={he.invoices.agingAria}
          items={(data?.aging ?? []).map((row, index) => ({
            key: row.bucket,
            label: <Num>{row.bucket}</Num>,
            value: row.agorot,
            valueLabel: (
              <>
                <Money agorot={row.agorot} /> <span className="sub">({row.count})</span>
              </>
            ),
            barClass: `o${index + 1}`,
            pressed: bucket === row.bucket,
            onClick: () => setBucket(bucket === row.bucket ? '' : row.bucket),
          }))}
        />
        <div className="ord-legend">
          <span>{he.invoices.agingLegend}</span>
          <i style={{ background: 'var(--ord-1)' }} />
          0–30
          <i style={{ background: 'var(--ord-4)' }} />
          90+
        </div>
      </Card>

      {/* ── לפי לקוח ───────────────────────────────────────────────── */}
      <Card span={7} className="bycust" labelledBy="h-bc">
        <CardHead titleId="h-bc" title={he.invoices.byCustomer} sub={he.invoices.byCustomerSub} />
        <div className="tbl-wrap">
          <table className="tbl compact">
            <thead>
              <tr>
                <th scope="col">{he.invoices.colCustomer}</th>
                <th scope="col" className="n">
                  {he.invoices.colOpenBalance}
                </th>
                <th scope="col" className="n">
                  {he.invoices.colOverdue}
                </th>
                <th scope="col" className="n">
                  {he.invoices.colDaysToPay}
                </th>
                <th scope="col">{he.invoices.colMethod}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.byCustomer ?? []).map((row) => (
                <tr key={row.customerId}>
                  <td>
                    <button type="button" className="cell-btn" onClick={() => setCustomer(row.customerId)}>
                      {row.customerName}
                    </button>{' '}
                    {row.hasStandingOrder ? (
                      <Chip tone="accent" small>
                        {he.invoices.standingOrder}
                      </Chip>
                    ) : null}
                  </td>
                  <td className="n">
                    <Money agorot={row.openAgorot} />
                  </td>
                  <td className="n">
                    {row.overdueAgorot ? (
                      <>
                        <span style={{ color: 'var(--critical)' }}>
                          <Money agorot={row.overdueAgorot} />
                        </span>{' '}
                        <span className="note">({row.overdueCount})</span>
                      </>
                    ) : (
                      <span className="note">{he.app.none}</span>
                    )}
                  </td>
                  <td className="n">
                    <Num>{row.avgDaysToPay ?? '—'}</Num>
                  </td>
                  <td>{PAYMENT_METHOD[row.method]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── רשימת חשבוניות ─────────────────────────────────────────── */}
      <Card span={12} className="list-c" labelledBy="h-il">
        <CardHead titleId="h-il" title={he.invoices.list} sub={he.invoices.listSub(data?.items.length ?? 0)} />
        {data?.stale ? <ErrorBox title={he.invoices.hypError} body={he.invoices.hypErrorBody(fmtDate(data.stale.fetchedAt?.slice(0, 10) ?? ''))} /> : null}
        <div className="chips" style={{ marginBottom: '8px' }}>
          <FilterChip label={he.invoices.filterAll} pressed={status === 'all'} onClick={() => setStatus('all')} />
          <FilterChip label={he.invoices.filterOverdue} count={data?.summary.overdueCount} pressed={status === 'overdue'} onClick={() => setStatus('overdue')} />
          <FilterChip
            label={he.invoices.filterOpen}
            count={(data?.summary.openCount ?? 0) - (data?.summary.overdueCount ?? 0)}
            pressed={status === 'open'}
            onClick={() => setStatus('open')}
          />
          <FilterChip label={he.invoices.filterPaid} pressed={status === 'paid'} onClick={() => setStatus('paid')} />
          <FilterChip label={he.invoices.filterCredited} pressed={status === 'credited'} onClick={() => setStatus('credited')} />
        </div>
        <div className="filters desk-only">
          <SelectField
            name="customer"
            label={he.invoices.colCustomer}
            value={customer}
            onChange={setCustomer}
            options={[['all', he.invoices.filterCustomerAll], ...(data?.byCustomer ?? []).map((c) => [c.customerId, c.customerName] as const)]}
          />
          <SelectField
            name="range"
            label={he.invoices.filterRange}
            value={range}
            onChange={setRange}
            options={[
              ['all', he.invoices.filterRangeAll],
              ['2026-09-01', 'ספטמבר 2026'],
              ['2026-07-01', 'מיולי 2026'],
              ['2026-05-01', 'ממאי 2026'],
            ]}
          />
          <SearchField value={q} onChange={setQ} placeholder={he.invoices.searchInvoice} label={he.invoices.searchInvoiceAria} />
          {bucket ? (
            <span className="chip accent">
              {he.invoices.bucketFilter(bucket)}
              <button type="button" className="link-btn" aria-label={he.invoices.clearBucket} onClick={() => setBucket('')}>
                <Icon name="x" className="xs" />
              </button>
            </span>
          ) : null}
        </div>

        {invoices.loading ? (
          <Skeleton className="ch" />
        ) : !data?.items.length ? (
          <EmptyState
            text={q || customer !== 'all' || range !== 'all' || bucket ? he.invoices.emptyFiltered : he.invoices.emptyNone}
            action={
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setStatus('all');
                  setCustomer('all');
                  setBucket('');
                  setRange('all');
                  setQ('');
                  navigate(buildPath('invoices'), { replace: true });
                }}
              >
                {he.app.clear}
              </button>
            }
          />
        ) : (
          <>
            <div className="tbl-wrap desk-only">
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">{he.invoices.colNumber}</th>
                    <SortableTh label={he.invoices.colIssue} col={'issue' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['issue', 'amount']))} />
                    <SortableTh label={he.invoices.colDue} col={'due' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['issue', 'amount']))} />
                    <SortableTh label={he.invoices.colCustomer} col={'customer' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['issue', 'amount']))} />
                    <th scope="col" className="n">
                      {he.invoices.colNet}
                    </th>
                    <th scope="col" className="n">
                      {he.invoices.colVat}
                    </th>
                    <SortableTh label={he.invoices.colGross} col={'amount' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['issue', 'amount']))} numeric />
                    <th scope="col">{he.invoices.colMethod}</th>
                    <th scope="col">{he.invoices.colStatus}</th>
                    <th scope="col">{he.invoices.colReceipt}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((invoice) => (
                    <tr
                      key={invoice.number}
                      className="clickable"
                      tabIndex={0}
                      onClick={() => openInvoice(invoice.number)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openInvoice(invoice.number);
                        }
                      }}
                    >
                      <td>
                        <Ltr className="mono">{invoice.number}</Ltr>
                        <span className="sub">{DOC_TYPE[invoice.docType].short}</span>
                      </td>
                      <td>
                        <Num>{fmtDate(invoice.issueDate)}</Num>
                      </td>
                      <td>{invoice.method === 'transfer' ? <Num>{fmtDate(invoice.dueDate)}</Num> : <span className="note">{he.invoices.immediate}</span>}</td>
                      <td>
                        {invoice.customerName}
                        {invoice.label ? <span className="sub">{invoice.label}</span> : null}
                      </td>
                      <td className="n">
                        <Money agorot={invoice.netAgorot} decimals={2} />
                      </td>
                      <td className="n">
                        <Money agorot={invoice.vatAgorot} decimals={2} />
                      </td>
                      <td className="n">
                        <b>
                          <Money agorot={invoice.grossAgorot} decimals={2} />
                        </b>
                      </td>
                      <td>{PAYMENT_METHOD[invoice.method]}</td>
                      <td>
                        <StatusChip invoice={invoice} />
                      </td>
                      <td>
                        {invoice.matchedTxId ? (
                          <Chip tone="good" icon="check" small>
                            {he.invoices.matched(fmtShortDate(invoice.paidAt ?? '', asOf))}
                          </Chip>
                        ) : (
                          <span className="note">{he.app.none}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="list mob-only" style={{ flexDirection: 'column', gap: '4px' }}>
              {data.items.map((invoice) => (
                <button key={invoice.number} type="button" className="list-item" onClick={() => openInvoice(invoice.number)}>
                  <div className="li-main">
                    <div className="li-t">
                      <Ltr className="mono">{invoice.number}</Ltr> · {invoice.customerName}
                    </div>
                    <div className="li-s">
                      {he.invoices.issuedOn(fmtShortDate(invoice.issueDate, asOf))} ·{' '}
                      {invoice.method === 'transfer' ? he.invoices.dueOn(fmtShortDate(invoice.dueDate, asOf)) : he.invoices.immediate} · {PAYMENT_METHOD[invoice.method]}
                    </div>
                  </div>
                  <div className="li-end">
                    <span className="li-amt">
                      <Money agorot={invoice.grossAgorot} />
                    </span>
                    <StatusChip invoice={invoice} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function ReceiptPicker({ candidates, onChange }: { candidates: ReceiptCandidate[]; onChange: (value: string | null) => void }) {
  const [value, setValue] = useState<string | null>(candidates[0]?.txId ?? null);
  return (
    <div className="pick" role="radiogroup" aria-label={he.invoices.matchReceipt}>
      {candidates.map((candidate) => (
        <label key={candidate.txId}>
          <input
            type="radio"
            name="receipt-candidate"
            checked={value === candidate.txId}
            onChange={() => {
              setValue(candidate.txId);
              onChange(candidate.txId);
            }}
          />
          <span>
            <Num>{fmtDate(candidate.day)}</Num> · {candidate.description}
          </span>
          <span className="pk-s">{fmtMoney(candidate.amountAgorot)}</span>
        </label>
      ))}
      <label>
        <input
          type="radio"
          name="receipt-candidate"
          checked={value === null}
          onChange={() => {
            setValue(null);
            onChange(null);
          }}
        />
        <span>{he.invoices.noReceiptOption}</span>
      </label>
    </div>
  );
}
